"use strict";
/**
 * This error class is designed to give consumers of Journey Micro
 * a consistent way of "throwing" errors. Specifically, these errors
 * will give clients to Journey Micro implementations two things:
 *
 * 1. An consistent, static error code by which to easily classify errors
 * 2. An error message intended for humans
 *
 * Errors will usually assume that there is some client side error and default to 400 for
 * a rest-like response. This can be changed however to more accurately, in restful terms,
 * indicate what went wrong.
 *
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResourceConflict = exports.ResourceNotFound = exports.InternalServerError = exports.AuthorizationError = exports.ValidationError = exports.JourneyError = exports.ErrorSeverity = void 0;
var ErrorSeverity;
(function (ErrorSeverity) {
    ErrorSeverity["INFO"] = "info";
    ErrorSeverity["WARNING"] = "warning";
    ErrorSeverity["ERROR"] = "error";
})(ErrorSeverity || (exports.ErrorSeverity = ErrorSeverity = {}));
class JourneyError extends Error {
    constructor(data) {
        super(`[${data.code}] ${data.description}\n  ${data.details}`);
        this.is_journey_error = true;
        this.errorData = data;
        if (data.stack) {
            this.stack = data.stack;
        }
        this.name = data.name || this.constructor.name;
        this.errorData.name = this.name;
    }
    toString() {
        return this.stack;
    }
    toJSON() {
        if (process.env.NODE_ENV !== 'production') {
            return this.errorData;
        }
        return {
            name: this.errorData.name,
            code: this.errorData.code,
            status: this.errorData.status,
            description: this.errorData.description,
            details: this.errorData.details,
            trace_id: this.errorData.trace_id,
            severity: this.errorData.severity,
            origin: this.errorData.origin
        };
    }
    setTraceId(id) {
        this.errorData.trace_id = id;
    }
}
exports.JourneyError = JourneyError;
class ValidationError extends JourneyError {
    constructor(errors) {
        super({
            code: ValidationError.CODE,
            status: 400,
            description: 'Validation failed',
            details: JSON.stringify(errors)
        });
    }
}
exports.ValidationError = ValidationError;
ValidationError.CODE = 'VALIDATION_ERROR';
class AuthorizationError extends JourneyError {
    constructor(errors) {
        super({
            code: AuthorizationError.CODE,
            status: 401,
            description: 'Authorization failed',
            details: errors
        });
    }
}
exports.AuthorizationError = AuthorizationError;
AuthorizationError.CODE = 'AUTHORIZATION';
class InternalServerError extends JourneyError {
    constructor(err) {
        super({
            code: InternalServerError.CODE,
            severity: ErrorSeverity.ERROR,
            status: 500,
            description: 'Something went wrong',
            details: err.message,
            stack: process.env.NODE_ENV !== 'production' ? err.stack : undefined
        });
    }
}
exports.InternalServerError = InternalServerError;
InternalServerError.CODE = 'INTERNAL_SERVER_ERROR';
class ResourceNotFound extends JourneyError {
    constructor(resource, id) {
        const combinedId = id ? `${resource}/${id}` : resource;
        super({
            code: ResourceNotFound.CODE,
            status: 404,
            description: 'The requested resource does not exist on this server',
            details: `The resource ${combinedId} does not exist on this server`,
            severity: ErrorSeverity.INFO
        });
    }
}
exports.ResourceNotFound = ResourceNotFound;
ResourceNotFound.CODE = 'RESOURCE_NOT_FOUND';
class ResourceConflict extends JourneyError {
    constructor(details) {
        super({
            code: ResourceConflict.CODE,
            status: 409,
            description: 'The specified resource already exists on this server',
            details: details,
            severity: ErrorSeverity.INFO
        });
    }
}
exports.ResourceConflict = ResourceConflict;
ResourceConflict.CODE = 'RESOURCE_CONFLICT';
